import { useEffect, useState } from 'react';
import { api, setToken, type LoginResult } from '../lib/api';

export function SignIn({ onSignedIn }: { onSignedIn: (r: LoginResult) => void }) {
  // One screen, two modes. A separate signup page means a person who mistyped
  // their password lands somewhere they have to navigate out of, and the two
  // forms differ by two fields.
  // Four states, one screen. A person who cannot get in should never have to
  // navigate anywhere to fix it — the reset arrives as a link back to this same
  // page, so the page has to know what to do with it.
  const [mode, setMode] = useState<'in' | 'up' | 'forgot' | 'choose'>('in');
  const [resetToken, setResetToken] = useState<string | null>(null);
  // Whether this deployment can actually send a reset. Starts false and is only
  // turned on by the API saying so — so the entrance is hidden while the answer
  // is unknown, and hidden if the check itself fails. The wrong default here is
  // the one that shows a locked-out person a button that returns a 503.
  const [canReset, setCanReset] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A reset link lands here as ?reset=<token>. The token is read once and then
  // REMOVED from the address bar: it is a credential that changes a password,
  // and leaving it in the URL puts it in browser history, in any bookmark, and
  // in anything the person pastes when asking for help.
  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('reset');
    if (!token) return;
    setResetToken(token);
    setMode('choose');
    url.searchParams.delete('reset');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, []);

  // The door's handoff lands here as ?handoff=<code>: a one-minute code from
  // "Open the studio" at rookeryos.com. Scrubbed from the address bar the way
  // the reset token is, before anything else, then exchanged for a session.
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('handoff');
    if (!code) return;
    url.searchParams.delete('handoff');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    setBusy(true);
    api.handoff(code)
      .then((r) => { setToken(r.token); onSignedIn(r); })
      .catch((err: Error) => { setError(err.message || 'the handoff expired — sign in instead'); setBusy(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    api.capabilities()
      .then((c) => { if (live) setCanReset(c.passwordReset); })
      .catch(() => { /* stays hidden — see the note on canReset */ });
    return () => { live = false; };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === 'forgot') {
        const r = await api.requestReset(email.trim());
        // Shown whatever the answer was. The API says the same thing for an
        // address that exists and one that does not, and repeating it verbatim
        // is what keeps that true all the way to the screen.
        setNotice(r.message);
        setBusy(false);
        return;
      }
      if (mode === 'choose') {
        await api.confirmReset(resetToken ?? '', password);
        setNotice('Your password is changed. Sign in with it.');
        setMode('in');
        setPassword('');
        setBusy(false);
        return;
      }
      const r = mode === 'in'
        ? await api.login(email.trim(), password)
        : await api.signup({
            email: email.trim(),
            password,
            name: name.trim() || undefined,
            workspace: workspace.trim(),
          });
      setToken(r.token);
      onSignedIn(r);
    } catch (err) {
      // The API returns one message for a wrong password and an unknown
      // address on purpose; show it as given rather than guessing which.
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <span className="mark" style={{ fontSize: 17 }}><span className="bars" />Rookery</span>
      <p style={{ color: 'var(--ink-2)', margin: '2px 0 4px', fontSize: 13.5 }}>
        {mode === 'in' ? 'Sign in to your workspace.'
          : mode === 'up' ? 'Create a workspace. Everything from idea to published video happens inside it.'
          : mode === 'forgot' ? 'Tell us the address you signed up with and we will send a link.'
          : 'Choose a new password.'}
      </p>
      <form onSubmit={(e) => void submit(e)}>
        {mode === 'up' && (
          <label htmlFor="workspace">
            Workspace
            <input id="workspace" type="text" required maxLength={120}
                   placeholder="Your channel, brand or company"
                   value={workspace} onChange={(e) => setWorkspace(e.target.value)} />
          </label>
        )}
        {mode !== 'choose' && (
        <label htmlFor="email">
          Email
          <input id="email" type="email" autoComplete="username" required
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        )}
        {mode !== 'forgot' && (
        <label htmlFor="password">
          Password
          <input id="password" type="password" required
                 autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
                 minLength={mode === 'up' || mode === 'choose' ? 12 : undefined}
                 value={password} onChange={(e) => setPassword(e.target.value)} />
          {(mode === 'up' || mode === 'choose') && (
            <small style={{ color: 'var(--ink-3)' }}>
              At least 12 characters. Length beats punctuation — a passphrase is fine.
            </small>
          )}
        </label>
        )}
        {mode === 'up' && (
          <label htmlFor="name">
            Your name <span style={{ color: 'var(--ink-3)' }}>(optional)</span>
            <input id="name" type="text" autoComplete="name" maxLength={120}
                   value={name} onChange={(e) => setName(e.target.value)} />
          </label>
        )}
        {error && <div className="err" style={{ marginTop: 4 }}>{error}</div>}
        {notice && <div className="notice">{notice}</div>}
        <button className="btn pri" type="submit"
                disabled={
                  busy
                  || (mode !== 'choose' && !email)
                  || (mode !== 'forgot' && !password)
                  || (mode === 'up' && !workspace.trim())
                }>
          {busy ? 'Working…'
            : mode === 'in' ? 'Sign in'
            : mode === 'up' ? 'Create workspace'
            : mode === 'forgot' ? 'Send a reset link'
            : 'Set the new password'}
        </button>
      </form>

      <p style={{ marginTop: 12, fontSize: 13, color: 'var(--ink-2)' }}>
        {mode === 'in' && (
          <>
            No workspace yet?{' '}
            <button type="button" className="linklike"
                    onClick={() => { setMode('up'); setError(null); setNotice(null); }}>
              Create one
            </button>
            {canReset && (
              <>
                {' · '}
                <button type="button" className="linklike"
                        onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}>
                  Forgot your password?
                </button>
              </>
            )}
          </>
        )}
        {mode !== 'in' && (
          <>
            Already have one?{' '}
            <button type="button" className="linklike"
                    onClick={() => { setMode('in'); setError(null); setNotice(null); }}>
              Sign in
            </button>
          </>
        )}
      </p>
    </div>
  );
}
